import React, { useState, useEffect, useRef  } from 'react';
import styled from 'styled-components';
import NISEdit from './nisEdit';
import NISAdd from './nisAdd';
import useNISData from '../Hooks/NisHooks/useNIS';
import useYearNISData from '../Hooks/NisHooks/useYearNISData';
import useDeleteNIS from '../Hooks/NisHooks/useDeleteNis';
import useSubtotalsByYear from '../Hooks/NisHooks/useNISSubtotals';
import adminBg from '../Assets/bg/adminbg.jpg';
import Loader from '../Components/Loader';
import DeleteConfirmationModal from '../Components/DeleteConfirmationModal';
import NisImport from './nisImport';
import NisModal from './nisModal';
import ExcelJS from 'exceljs';

const NIS = ({ darkMode, onSelect, isSidebarExpanded = false }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showEditPanel, setShowEditPanel] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedYear, setSelectedYear] = useState(null);
  const { data: yearData, loading: yearLoading, error: yearError } = useYearNISData();
  const { data, loading, error, refetch } = useNISData(selectedYear);
  const { data: subtotals, loading: subtotalsLoading, error: subtotalsError } = useSubtotalsByYear(selectedYear);
  const [modalOpen, setModalOpen] = useState(false);
  const [showRightPanel, setShowRightPanel] = useState(false);
  const [showImportPanel, setShowImportPanel] = useState(false);
  const fileInputRef = useRef(null);
  const [showAllData, setShowAllData] = useState(false);
  const { deleteNIS, loading: deleteLoading, error: deleteError, successMessage } = useDeleteNIS();
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [headerImo, setHeaderImo] = useState(localStorage.getItem('headerImo') || 'BOHOL-CEBU-SIQUIJOR IMO');
  const [headerProfileDate, setHeaderProfileDate] = useState(localStorage.getItem('headerProfileDate') || 'CIS IA PROFILE as of March 31, 2025');
  const [headerRegion, setHeaderRegion] = useState(localStorage.getItem('headerRegion') || 'Region: 7');
  const [isEditingHeader, setIsEditingHeader] = useState(false);
  const [addSuccessMessage, setAddSuccessMessage] = useState(null);
  const saveHeader = () => {
    localStorage.setItem('headerImo', headerImo);
    localStorage.setItem('headerProfileDate', headerProfileDate);
    localStorage.setItem('headerRegion', headerRegion);
    setIsEditingHeader(false);
  };

  // Role-based UI gating: NOSO should only be able to edit on NOSO pages.
  const role = (localStorage.getItem('role') || '').toLowerCase();
  const section = 'nis';
  const isAdmin = role === 'admin';
  const isNoso = role === 'noso';
  // Allow bohol role to manage NIS as well (they can edit CIS Bohol + NIS)
  const showControls = isAdmin || (isNoso && section === 'noso') || (role === section && role !== 'noso') || role === 'bohol';
  const hideControlsForViewOnly = !showControls && ['bohol', 'siquijor', 'nis'].includes(section) ? false : !showControls;

  const handleImportSuccess = async (newRecords) => {
    // Simulate API call to save new records (replace with actual API call)
    console.log('Saving imported records:', newRecords);
    setAddSuccessMessage('Data imported successfully!');
    setTimeout(() => setAddSuccessMessage(null), 3000);
    setShowImportPanel(false);
    await refetch();
  };
  
  const handleAddSuccess = (msg) => {
    setAddSuccessMessage(msg || 'NIS data successfully added!');
    setTimeout(() => setAddSuccessMessage(null), 3000); // hide after 3s
    refetch(); // refresh your table data
  };

  console.log('Year Data:', yearData, 'Year Error:', yearError, 'Selected Year:', selectedYear);
  console.log('Subtotals:', subtotals);

  useEffect(() => {
    if (yearData && yearData.length > 0) {
      const latestYear = yearData
        .map(item => item.years)
        .sort((a, b) => b - a)[0];
      console.log('Latest year:', latestYear);
      setSelectedYear(prev => prev ?? latestYear);
    }
  }, [yearData]);

  useEffect(() => {
    const scrollContent = document.getElementById('scrollContent');
    const bottomScroll = document.getElementById('bottomScroll');

    if (scrollContent && bottomScroll) {
      const onBottomScroll = () => {
        scrollContent.scrollLeft = bottomScroll.scrollLeft;
      };

      const onContentScroll = () => {
        bottomScroll.scrollLeft = scrollContent.scrollLeft;
      };

      bottomScroll.addEventListener('scroll', onBottomScroll);
      scrollContent.addEventListener('scroll', onContentScroll);

      return () => {
        bottomScroll.removeEventListener('scroll', onBottomScroll);
        scrollContent.removeEventListener('scroll', onContentScroll);
      };
    }
  }, []);

  const handleClose = () => {
    setShowRightPanel(false);
    console.log('Form closed');
  };

  const handleRowClick = (item) => {
    console.log("Opening modal with year:", selectedYear);
    setSelectedItem(item);
    setModalOpen(true);
  };

  const handleEditData = (item) => {
    console.log('Editing IA ID:', item.id);
    setSelectedItem(item);
    setShowEditPanel(true);
  };

  const closeEditPanel = () => {
    setShowEditPanel(false);
    setSelectedItem(null);
  };

  const handleDeleteClick = (item) => {
    setConfirmDelete(item);
  };

 const flattenedData = data
    ? data.flatMap(isItem =>
        (isItem.ias || []).flatMap(ia =>
          (ia.rows || []).map(row => ({
            ...row,
            id: row.id,
            ia_name: ia.ia_name,
            is_name: isItem.is_name,
            createdAt: row.createdAt || isItem.createdAt || ia.createdAt
          }))
        )
      ).sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
    : [];


  console.log('Flattened Data:', flattenedData);

  const filteredData = flattenedData.filter(item =>
    Object.values(item).some(val =>
      (val?.toString()?.toLowerCase() || '').includes(searchQuery.toLowerCase())
    )
  );

const isOrder = [...new Set(flattenedData.map(item => item.is_name))];

  const uniqueISGroups = Object.entries(
 filteredData.reduce((acc, item) => {
      const group = item.is_name || 'Unknown';
      if (!acc[group]) acc[group] = { items: [], subtotal: null };
      acc[group].items.push(item);
      return acc;
    }, {})
  ).sort(([a], [b]) => isOrder.indexOf(a) - isOrder.indexOf(b));

  // Map subtotals to IS groups
  if (subtotals) {
    uniqueISGroups.forEach(([is_name, group]) => {
      const subtotalKey = is_name;
      group.subtotal = subtotals[subtotalKey]?.subtotal || null;
    });
  }


 const subtotalByGroup = subtotals
    ? Object.entries(subtotals).reduce((acc, [key, value]) => {
        if (key === 'grandTotal') {
          acc.grandTotal = value;
        } else {
          acc.subtotals[key] = value.subtotal;
        }
        return acc;
      }, { subtotals: {}, grandTotal: null })
    : { subtotals: {}, grandTotal: null };


  const exportGroups = showAllData
    ? uniqueISGroups
    : uniqueISGroups.slice(0, 1).map(([is_name, group]) => [is_name, { ...group, items: group.items.slice(0, 8) }]);

  const shouldShowButton = uniqueISGroups.length > 1 || uniqueISGroups.some(([, group]) => group.items.length > 8);

  const handleManageData = (value) => {
    if (value === 'import') {
      fileInputRef.current.click(); // Trigger file input click
    } else if (value === 'export') {
      handleExport();
    }
  };

  const handleImportFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file); // Store the selected file
      setShowImportPanel(true); // Show import panel after file selection
      event.target.value = null; // Reset file input
    }
  };


  const handleExport = async () => {
    
const exportGroups = uniqueISGroups || [];
  // basic safeguard
  if (!headerImo || !headerProfileDate || !exportGroups || !subtotalByGroup) {
    console.error("Missing required data for export.");
    return;
  }

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('NIS');

  // helper to avoid throwing when a range is already merged
  const safeMerge = (range) => {
    try {
      worksheet.mergeCells(range);
    } catch (err) {
      // ignore the specific "already merged" error, rethrow otherwise
      if (err && /already merged/i.test(err.message)) {
        console.warn(`Skipped merge (already merged): ${range}`);
      } else {
        console.error(`Merge failed for ${range}`, err);
        throw err;
      }
    }
  };

  // Header rows (use CIS-style header fields)
  safeMerge('A1:Q1');
  const titleCell = worksheet.getCell('A1');
  titleCell.value = headerImo;
  titleCell.font = { name: 'Calibri', size: 18, bold: true };
  titleCell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };

  safeMerge('A2:Q2');
  const a2 = worksheet.getCell('A2');
  a2.value = headerProfileDate;
  a2.font = { name: 'Calibri', size: 18 };
  a2.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };

  safeMerge('A3:Q3');
  const a3 = worksheet.getCell('A3');
  a3.value = headerRegion;
  a3.font = { name: 'Calibri', size: 18 };
  a3.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };

  worksheet.addRow([]);

   // Main headers
  const mainHeaders = [
    'NO', 'NAME OF IRRIGATORS\' ASSOCIATION', 'MUNICIPALITY', 'DISTRICT NO.', 'MAILING ADDRESS',
    'NAME OF IA PRESIDENT', 'CONTACT NUMBER', 'DATE ORGANIZED', 'SEC / CDA/DOLE REG. NO.', '',
    'IA TIN', 'MEMBERSHIP', '', 'SERVICE AREA', 'FUSA', 'IRRIGATED AREA', 'OPERATIONAL AREA'
  ];
  const mainHeaderRow = worksheet.addRow(mainHeaders);


  mainHeaderRow.eachCell((cell, colNumber) => {
    cell.font = { size: 12, bold: true, color: { argb: 'FFFFFFFF' } }; // white text
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2E7D32' } }; // full ARGB
    cell.alignment = { horizontal: 'center', wrapText: true };
    cell.border = {
      top: { style: 'thin', color: { argb: 'FF000000' } },
      bottom: { style: 'thin', color: { argb: 'FF000000' } },
      left: { style: 'thin', color: { argb: 'FF000000' } },
      right: { style: 'thin', color: { argb: 'FF000000' } },
    };
  });
  
// Do merges for main header columns exactly once (avoid merging inside eachCell)
  safeMerge(`I${mainHeaderRow.number}:J${mainHeaderRow.number}`);
  safeMerge(`L${mainHeaderRow.number}:M${mainHeaderRow.number}`);

  // Subheaders
  const subHeaders = ['', '', '', '', '', '', '', '', 'NUMBER', 'DATE', '', 'POT\'L', 'ACTUAL', '', '', '', ''];
  const subHeaderRow = worksheet.addRow(subHeaders);

  subHeaderRow.eachCell((cell, colNumber) => {
    cell.font = { size: 12, bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4CAF50' } };
    cell.alignment = { horizontal: 'center', wrapText: true };
    cell.border = {
      top: { style: 'thin', color: { argb: 'FF000000' } },
      bottom: { style: 'thin', color: { argb: 'FF000000' } },
      left: { style: 'thin', color: { argb: 'FF000000' } },
      right: { style: 'thin', color: { argb: 'FF000000' } },
    };
  });

  // merge O:Q for subheader once
  safeMerge(`O${subHeaderRow.number}:Q${subHeaderRow.number}`);

  // Data rows
  let rowIndex = 1;
  exportGroups.forEach(([is_name, group]) => {
    const groupRow = worksheet.addRow([is_name.toUpperCase()]);
    safeMerge(`A${groupRow.number}:Q${groupRow.number}`);
    groupRow.eachCell(cell => {
      cell.font = { size: 12, bold: true };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFCADABA' } };
      cell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
      cell.border = {
        top: { style: 'thin', color: { argb: 'FF000000' } },
        bottom: { style: 'thin', color: { argb: 'FF000000' } },
        left: { style: 'thin', color: { argb: 'FF000000' } },
        right: { style: 'thin', color: { argb: 'FF000000' } },
      };
    });


     let currentIA = null;
    group.items.forEach((item) => {
      const row = worksheet.addRow([
        rowIndex++,
        item.ia_name,
        item.municipality,
        item.districtNum,
        item.mailing_address,
        item.ia_president,
        item.contactNum,
        item.dateOrganized,
        item.secNum,
        item.secDate,
        item.ia_tin,
        item.membership_potl,
        item.membership_actual,
        item.serviceArea,
        item.fusa,
        item.irrigated_area,
        item.operational_area
      ]);

       
      row.eachCell((cell, colNumber) => {
        cell.font = { size: 12 };
        cell.border = {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } },
        };

        if ([3, 4, 6, 7].includes(colNumber)) {
          cell.alignment = { horizontal: 'left', vertical: 'top', wrapText: true };
        } else if ([14, 15, 16, 17].includes(colNumber)) {
          cell.alignment = { horizontal: 'right', vertical: 'top', wrapText: true };
        } else {
          cell.alignment = { horizontal: 'center', vertical: 'top', wrapText: true };
        }
      });

        if (item.ia_name !== currentIA) {
        currentIA = item.ia_name;
        const subtotalKey = `${is_name}_${item.ia_name}`;
        const subtotal = subtotalByGroup.subtotals && subtotalByGroup.subtotals[subtotalKey];
        if (subtotal) {
          const subtotalRow = worksheet.addRow([
            '', `Subtotal: ${subtotal.ia_name}`, '', '', '', '', '', '', '', '', '',
            subtotal.membership_potl, subtotal.membership_actual, subtotal.serviceArea,
            subtotal.fusa, subtotal.irrigated_area, subtotal.operational_area
          ]);
          safeMerge(`C${subtotalRow.number}:I${subtotalRow.number}`);
          subtotalRow.eachCell((cell, colNumber) => {
            cell.font = { size: 12, bold: true };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF00' } };
            cell.alignment = { horizontal: colNumber >= 15 ? 'right' : 'left', vertical: 'middle' };
            cell.border = {
              top: { style: 'thin', color: { argb: 'FF000000' } },
              bottom: { style: 'thin', color: { argb: 'FF000000' } },
              left: { style: 'thin', color: { argb: 'FF000000' } },
              right: { style: 'thin', color: { argb: 'FF000000' } },
            };
          });
          worksheet.addRow([]);
        }
      }
    });
      if (group.subtotal) {
      const isSubtotalRow = worksheet.addRow([
        '', `Subtotal: ${is_name}`, '', '', '', '', '', '', '', '', '',
        group.subtotal.membership_potl, group.subtotal.membership_actual, group.subtotal.serviceArea,
        group.subtotal.fusa, group.subtotal.irrigated_area, group.subtotal.operational_area
      ]);
      safeMerge(`C${isSubtotalRow.number}:I${isSubtotalRow.number}`);
      isSubtotalRow.eachCell((cell, colNumber) => {
        cell.font = { size: 12, bold: true };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF00' } };
        cell.alignment = { horizontal: colNumber >= 15 ? 'right' : 'left', vertical: 'middle' };
        cell.border = {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } },
        };
      });
      worksheet.addRow([]);
    }
  });

    
    // Grand total
  if (subtotalByGroup.grandTotal) {
    const g = subtotalByGroup.grandTotal;
    const grandTotalRow = worksheet.addRow([
      '', `Grand Total: ${g.ia_name}`, '', '', '', '', '', '', '', '', '',
      g.membership_potl, g.membership_actual, g.serviceArea, g.fusa, g.irrigated_area, g.operational_area
    ]);
    safeMerge(`C${grandTotalRow.number}:I${grandTotalRow.number}`);
    grandTotalRow.eachCell((cell, colNumber) => {
      cell.font = { size: 14, bold: true };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF00' } };
      cell.alignment = { horizontal: colNumber >= 15 ? 'right' : 'left', vertical: 'middle' };
      cell.border = {
        top: { style: 'thin', color: { argb: 'FF000000' } },
        bottom: { style: 'thin', color: { argb: 'FF000000' } },
        left: { style: 'thin', color: { argb: 'FF000000' } },
        right: { style: 'thin', color: { argb: 'FF000000' } },
      };
    });
    worksheet.addRow([]);
  }

    // columns
  worksheet.columns = [
    { key: 'no', width: 5 },
    { key: 'ia_name', width: 30 },
    { key: 'municipality', width: 25 },
    { key: 'districtNum', width: 10 },
    { key: 'mailing_address', width: 25 },
    { key: 'ia_president', width: 25 },
    { key: 'contactNum', width: 25 },
    { key: 'dateOrganized', width: 15 },
    { key: 'secNum', width: 15 },
    { key: 'secDate', width: 10 },
    { key: 'ia_tin', width: 15 },
    { key: 'membership_potl', width: 10 },
    { key: 'membership_actual', width: 10 },
    { key: 'serviceArea', width: 15 },
    { key: 'fusa', width: 10 },
    { key: 'irrigated_area', width: 15 },
    { key: 'operational_area', width: 15 },
  ];

    // Save file (same as before)
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

  if (window.isSecureContext && 'showSaveFilePicker' in window) {
    try {
      const handle = await window.showSaveFilePicker({
        suggestedName: 'IA_Profile_BIIS_2025.xlsx',
        types: [{ description: 'Excel File', accept: { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] } }]
      });
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
    } catch (err) {
      console.error('Error saving file:', err);
      if (err.name !== 'AbortError') alert('Save failed due to an unexpected error. Check the console.');
    }
  } else {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'IA_Profile_BIIS_2025.xlsx';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }
};

    if (yearLoading || loading || subtotalsLoading) {
      return (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          backgroundColor: darkMode ? 'black' : 'white',
        }}>
          <Loader />
        </div>
      );
    }

    if (yearError || error || subtotalsError) {
      return <div>Error: {yearError || error || subtotalsError}</div>;
    }

    return (
    <>
      {addSuccessMessage && (
        <div
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            backgroundColor: '#4caf50',
            color: '#fff',
            padding: '10px 20px',
            borderRadius: '5px',
            boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
            zIndex: 9999
          }}
        >
          {addSuccessMessage}
        </div>
      )}
      <SlidingPanel darkMode={darkMode} show={true} isExpanded={isSidebarExpanded}>
        <div style={{ display: 'flex', height: '100vh' }}>

        {showImportPanel ? (
                  <Container darkMode={darkMode} isSidebarExpanded={isSidebarExpanded} showRightPanel={false}>
       <NisImport
          darkMode={darkMode}
          onClose={() => setShowImportPanel(false)}
          onSave={handleImportSuccess}
          selectedYear={selectedYear}
          setSelectedYear={setSelectedYear}
          importedFile={selectedFile} // ✅ Correct usage now
        />
         
          </Container>
        ) : (
          <>
          <Container darkMode={darkMode} tableisSidebarExpanded={isSidebarExpanded} showRightPanel={showRightPanel || showEditPanel}>
            <HeaderContainer>
            <HeaderLeft>
                <HeaderText>
                  {isEditingHeader ? (
                      <>
                        <input value={headerImo} onChange={(e) => setHeaderImo(e.target.value)} />
                        <input value={headerProfileDate} onChange={(e) => setHeaderProfileDate(e.target.value)} />
                        <input value={headerRegion} onChange={(e) => setHeaderRegion(e.target.value)} />
                        <button onClick={saveHeader}>Save</button>
                        <button onClick={() => setIsEditingHeader(false)}>Cancel</button>
                      </>
                    ) : (
                      <>
                        <h2>{headerImo}</h2>
                        <p>{headerProfileDate}</p>
                        <p>{headerRegion}</p>
                        <p>PROVINCE 1: BOHOL</p>
                        <button onClick={() => setIsEditingHeader(true)}>Edit</button>
                      </>
                    )}
                </HeaderText>
                <YearGroup>
                  <label htmlFor="yearSelect">Year:</label>
                  <YearSelect
                    id="yearSelect"
                    value={selectedYear || ''}
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                  >
                    <option value="" disabled>Select Year</option>
                    {yearData && yearData.length === 0 && <option disabled>No years available</option>}
                    {yearData && yearData.map((item, index) => (
                      <option key={index} value={item.years}>{item.years}</option>
                    ))}
                  </YearSelect>
                </YearGroup>
              </HeaderLeft>
              {showControls && (
                <RightControls>
                  <SearchInput
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <StyledButton onClick={() => setShowRightPanel(true)}>
                    Add NIS
                  </StyledButton>
                  <ManageDataSelect onChange={(e) => handleManageData(e.target.value)}>
                    <option value="">Manage Data</option>
                    <option value="import">Import</option>
                    <option value="export">Export</option>
                  </ManageDataSelect>
                  <input
                    type="file"
                    accept=".xlsx"
                    onChange={handleImportFileSelect}
                    style={{ display: 'none' }}
                    ref={fileInputRef}
                  />
                </RightControls>
              )}
            </HeaderContainer>
            <TableContainer>
              <TableWrapper isExpanded={isSidebarExpanded} id="tableWrapper">
                <div id="scrollContent" style={{ width: '100%', overflowX: 'auto' }}>
                  <Table darkMode={darkMode}>
                    <thead>
                      <tr>
                        <TableHeader darkMode={darkMode}>ACTIONS</TableHeader>
                        <TableHeader darkMode={darkMode}>NO</TableHeader>
                        <TableHeader darkMode={darkMode}>NAME OF IRRIGATORS' ASSOCIATION</TableHeader>
                        <TableHeader darkMode={darkMode}>MUNICIPALITY</TableHeader>
                        <TableHeader darkMode={darkMode}>DISTRICT NO.</TableHeader>
                      <TableHeader darkMode={darkMode}>MAILING ADDRESS</TableHeader>
                      <TableHeader darkMode={darkMode}>NAME OF IA PRESIDENT</TableHeader>
                      <TableHeader darkMode={darkMode}>CONTACT NUMBER</TableHeader>
                      <TableHeader darkMode={darkMode}>DATE ORGANIZED</TableHeader>
                      <TableHeader darkMode={darkMode} colSpan="2" style={{ textAlign: 'center' }}>
                        SEC / CDA/DOLE REG. NO.
                      </TableHeader>
                      <TableHeader darkMode={darkMode}>IA TIN</TableHeader>
                      <TableHeader darkMode={darkMode} colSpan="2" style={{ textAlign: 'center' }}>
                        MEMBERSHIP
                      </TableHeader>
                      <TableHeader darkMode={darkMode}>SERVICE AREA</TableHeader>
                      <TableHeader darkMode={darkMode}>FUSA</TableHeader>
                      <TableHeader darkMode={darkMode}>IRRIGATED AREA</TableHeader>
                      <TableHeader darkMode={darkMode}>OPERATIONAL AREA</TableHeader>
                    </tr>
                    <tr>
                      <TableHeaderS darkMode={darkMode}></TableHeaderS>
                      <TableHeaderS colSpan="1" darkMode={darkMode}></TableHeaderS>
                      <TableHeaderS colSpan="7" darkMode={darkMode}></TableHeaderS>
                      <TableHeaderS darkMode={darkMode}>NUMBER</TableHeaderS>
                      <TableHeaderS darkMode={darkMode}>DATE</TableHeaderS>
                      <TableHeaderS colSpan="1" darkMode={darkMode}></TableHeaderS>
                      <TableHeaderS darkMode={darkMode}>POT'L</TableHeaderS>
                      <TableHeaderS darkMode={darkMode}>ACTUAL</TableHeaderS>
                      <TableHeaderS colSpan="4" darkMode={darkMode}></TableHeaderS>
                    </tr>
                  </thead>
                  <tbody>
                      {exportGroups.map(([is_name, group]) => {
                      let previousIA = null;
                      return (
                        <React.Fragment key={is_name}>
                          <tr>
                            <td
                              colSpan="100%"
                              style={{
                                backgroundColor: '#cadaba',
                                fontWeight: 'bold',
                                textAlign: 'left',
                                paddingLeft: '10px'
                              }}
                            >
                              {is_name.toUpperCase()}
                            </td>
                          </tr>
                           {group.items.reduce((acc, item, index) => {
                            const showIA = item.ia_name !== previousIA;
                            previousIA = item.ia_name;
                            acc.push(
                              <TableRow
                                key={`${item.id}-${index}`}
                                darkMode={darkMode}
                                onClick={() => handleRowClick(item)}
                              >
                                <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px', padding: '8px 0' }}>
                                  {showControls ? (
                                    <ButtonGroup>
                                      <StyledButton onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditData(item);
                                      }}>
                                        ✎
                                      </StyledButton>
                                      <StyledButton onClick={(e) => { e.stopPropagation(); handleDeleteClick(item); }}>❌</StyledButton>
                                    </ButtonGroup>
                                  ) : (
                                    <ButtonGroup>
                                      <StyledButton style={{ visibility: 'hidden' }}>✎</StyledButton>
                                      <StyledButton style={{ visibility: 'hidden' }}>❌</StyledButton>
                                    </ButtonGroup>
                                  )}
                                </TableData>
                                <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '40px' }}>
                                  {index + 1}
                                </TableData>
                                <TableData darkMode={darkMode} style={{ textAlign: 'left', minWidth: '240px' }}>
                                  {showIA ? item.ia_name : ''}
                                </TableData>
                                <TableData darkMode={darkMode} style={{ textAlign: 'left', minWidth: '200px' }}>
                                  {item.municipality}
                                </TableData>
                                <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '80px' }}>
                                  {item.districtNum}
                                </TableData>
                                <TableData darkMode={darkMode} style={{ textAlign: 'left', minWidth: '200px' }} className="station-mail">
                                  {item.mailing_address}
                                </TableData>
                                <TableData darkMode={darkMode} style={{ textAlign: 'left', minWidth: '200px' }} className="station-iaPres">
                                  {item.ia_president}
                                </TableData>
                                <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '200px' }}>
                                  {item.contactNum}
                                </TableData>
                                <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px' }}>
                                  {item.dateOrganized}
                                </TableData>
                                <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px' }} className="station-secNum">
                                  {item.secNum}
                                </TableData>
                                <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '80px' }} className="station-secDate">
                                  {item.secDate}
                                </TableData>
                                <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px' }} className="station-iaTin">
                                  {item.ia_tin}
                                </TableData>
                                <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '80px' }}>
                                  {item.membership_potl}
                                </TableData>
                                <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '80px' }}>
                                  {item.membership_actual}
                                </TableData>
                                <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px' }}>
                                  {item.serviceArea}
                                </TableData>
                                <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '80px' }}>
                                  {item.fusa}
                                </TableData>
                                <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px' }}>
                                  {item.irrigated_area}
                                </TableData>
                                <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px' }}>
                                  {item.operational_area}
                                </TableData>
                              </TableRow>
                            );

                            if (showIA) {
                              const subtotalKey = `${is_name}_${item.ia_name}`;
                              const subtotal = subtotalByGroup.subtotals[subtotalKey];
                              if (subtotal) {
                                acc.push(
                                  <tr key={`subtotal-ia-${subtotalKey}`}>
                                    <td colSpan="12" style={{ backgroundColor: '#ffff00', fontWeight: 'bold', textAlign: 'left', paddingLeft: '10px' }}>
                                      Subtotal: {subtotal.ia_name}
                                    </td>
                                     <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '80px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }}>
                                      {subtotal.membership_potl}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '80px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }}>
                                      {subtotal.membership_actual}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }}>
                                      {subtotal.serviceArea}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '80px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }}>
                                      {subtotal.fusa}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }}>
                                      {subtotal.irrigated_area}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }}>
                                      {subtotal.operational_area}
                                    </TableData>
                                  </tr>,
                                  <tr key={`spacer-ia-${subtotalKey}`} style={{ height: '10px' }}><td colSpan="100%"></td></tr>
                                );
                              }
                            }
                            return acc;
                          }, [])}

                           {group.subtotal && (
                            <>
                              <tr key={`subtotal-is-${is_name}`}>
                                <td colSpan="12" style={{ backgroundColor: '#ffff00', fontWeight: 'bold', textAlign: 'left', paddingLeft: '10px' }}>
                                  Subtotal: {is_name}
                                </td>
                                <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '80px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }}>
                                  {group.subtotal.membership_potl}
                                </TableData>
                                <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '80px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }}>
                                  {group.subtotal.membership_actual}
                                </TableData>
                                <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }}>
                                  {group.subtotal.serviceArea}
                                </TableData>
                                <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '80px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }}>
                                  {group.subtotal.fusa}
                                </TableData>
                                <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }}>
                                  {group.subtotal.irrigated_area}
                                </TableData>
                                <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }}>
                                  {group.subtotal.operational_area}
                                </TableData>
                              </tr>
                              <tr key={`spacer-is-${is_name}`} style={{ height: '10px' }}><td colSpan="100%"></td></tr>
                            </>
                          )}
                        </React.Fragment>
                      );
                    })}
                    {subtotalByGroup.grandTotal && (
                      <>
                        <tr>
                          <td
                            colSpan="12"
                            style={{
                               backgroundColor: '#ffff00',
                              fontWeight: 'bold',
                              textAlign: 'left',
                              paddingLeft: '10px',
                               fontSize: '14px'
                            }}
                          >
                          Grand Total: {subtotalByGroup.grandTotal.ia_name}
                          </td>
                          <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '80px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '14px' }}>
                            {subtotalByGroup.grandTotal.membership_potl}
                          </TableData>
                          <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '80px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '14px' }}>
                            {subtotalByGroup.grandTotal.membership_actual}
                          </TableData>
                          <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '14px' }}>
                            {subtotalByGroup.grandTotal.serviceArea}
                          </TableData>
                          <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '80px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '14px' }}>
                            {subtotalByGroup.grandTotal.fusa}
                          </TableData>
                          <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '14px' }}>
                            {subtotalByGroup.grandTotal.irrigated_area}
                          </TableData>
                          <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '14px' }}>
                            {subtotalByGroup.grandTotal.operational_area}
                          </TableData>
                        </tr>
                        <tr style={{ height: '10px' }}><td colSpan="100%"></td></tr>
                      </>
                    )}
                  </tbody>
                </Table>
                {!showAllData && shouldShowButton && (
                  <div style={{ textAlign: 'center', marginTop: '10px' }}>
                    <ShowAllButton onClick={() => setShowAllData(true)}>
                      Show All Data
                    </ShowAllButton>
                  </div>
                )}
                {showAllData && shouldShowButton && (
                  <div style={{ textAlign: 'center', marginTop: '10px' }}>
                    <ShowAllButton onClick={() => setShowAllData(false)}>
                      Show Less Data
                    </ShowAllButton>
                  </div>
                )}
              </div>
            </TableWrapper>
            <div
              style={{
                overflowX: 'auto',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
              }}
              className="hide-scrollbar"
              id="bottomScroll"
            >
              <div style={{ width: '2000px', height: '1px' }} />
            </div>
            {confirmDelete && (
              <DeleteConfirmationModal
                itemName={confirmDelete.ia_name}
                onCancel={() => setConfirmDelete(null)}
                onConfirm={async () => {
                  await deleteNIS(confirmDelete.id);
                  refetch();
                  setConfirmDelete(null);
                }}
              />
            )}
  
            <NisModal
              isOpen={modalOpen}  
              onClose={() => setModalOpen(false)}
              selectedItem={selectedItem}
              onNavigate={onSelect}
              selectedYear={selectedYear}
            />
            {showEditPanel && selectedItem && selectedItem.id && (
              <NISEdit
                selectedItem={selectedItem}
                onClose={closeEditPanel}
                darkMode={darkMode}
              />
            )}
          </TableContainer>
        </Container>
        <RightPanel darkMode={darkMode} show={showRightPanel}>
        <NISAdd 
          darkMode={darkMode} 
          onClose={handleClose} 
          onAddSuccess={handleAddSuccess} />   
   </RightPanel>
       </>
     )}
    </div>
    </SlidingPanel>
    </>
  );
};

export default NIS;

// Styled Components
const Container = styled.div`
  background-color: ${({ darkMode }) => (darkMode ? 'transparent' : '#f2f2f2')};
  background-image: ${({ darkMode }) => (darkMode ? `url(${adminBg})` : 'none')};
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  color: ${({ darkMode }) => (darkMode ? '#bdb9b9ff' : '#333')};
  padding: 80px 20px 20px;
  width: ${({ isSidebarExpanded, showRightPanel }) => (showRightPanel ? '57%' : isSidebarExpanded ? 'calc(100% - 100px)' : '98%')};
  min-height: 100vh;
  overflow-y: auto;
  margin-left: ${({ isSidebarExpanded }) => (isSidebarExpanded ? '110px' : '10px')};
  box-shadow: -4px 0 10px rgba(0, 0, 0, 0.2);
  transition: margin-left 0.3s ease, width 0.3s ease;
  box-sizing: border-box;
`;

const SlidingPanel = styled.div`
  position: fixed;
  top: 0;
  left: ${({ isExpanded }) => (isExpanded ? '180px' : '60px')};
  width: ${({ isExpanded }) => (isExpanded ? 'calc(100% - 180px)' : 'calc(100% - 60px)')};
  height: 100%;
  background-color: ${({ darkMode }) => (darkMode ? 'transparent' : '#f2f2f2')};
  background-image: ${({ darkMode }) => (darkMode ? `url(${adminBg})` : 'none')};
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  box-shadow: -4px 0 10px rgba(0, 0, 0, 0.2);
  transform: translateX(100%);
  transition: transform 0.5s ease, left 0.3s ease-in-out, width 0.3s ease-in-out;
  z-index: 2;
  padding: 20px;
  box-sizing: border-box;

  ${({ show }) => show && `transform: translateX(0);`}
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 0 40px;
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const HeaderText = styled.div`
  margin-bottom: 10px;
  margin-top: -38px;

  h2 {
    margin: 0;
    font-size: 22px;
    color: ${({ darkMode }) => (darkMode ? '#ffffff' : '#333')};
  }

  p {
    margin: 0;
    font-size: 18px;
    color: ${({ darkMode }) => (darkMode ? '#cccccc' : '#555')};
  }
`;

const YearGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ManageDataSelect = styled.select`
  padding: 6px 10px;
  background-color: #eee;
  border: 1px solid #aaa;
  border-radius: 4px;
  color: #333;
  cursor: pointer;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #388e3c;
  }

  &:hover {
    background-color: #ddd;
  }

  option {
    color: #333;
    background-color: #fff;
  }
`;

const RightControls = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-end;

`;

const StyledButton = styled.button`
  margin: 0 2px;
  padding: 6px 10px;
  background-color: #eee;
  border: 1px solid #aaa;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
  font-size: 14px;

  &:hover {
    background-color: #ddd;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
`;

const ShowAllButton = styled.button`
  margin: 20px auto;
  display: block;
  padding: 8px 16px;
  background-color: #eee;
  border: 1px solid #aaa;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #ddd;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #4caf50;
  border-radius: 4px;
  background-color: #ffffff;
  color: #333;
  font-size: 14px;
  width: 100%;
  transition: border-color 0.2s ease, background-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #388e3c;
  }

  &:hover {
    background-color: #f1f1f1;
  }

  &::placeholder {
    color: #888;
    font-style: italic;
    font-size: 14px;
  }
`;

const YearSelect = styled.select`
  padding: 6px 10px;
  border: 1px solid #4caf50;
  border-radius: 4px;
  background-color: #ffffff;
  color: #333;
  cursor: pointer;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #388e3c;
  }

  &:hover {
    background-color: #f1f1f1;
  }

  option {
    color: #333;
    background-color: #fff;
  }
`;

const TableWrapper = styled.div`
  width: 100%;
  height: auto;
  overflow-y: visible;
  overflow-x: auto;
  margin-top: 10px;
  position: relative;
  background-color: ${({ darkMode }) => (darkMode ? 'rgb(42, 48, 42)' : '#f2f2f2')};

  scrollbar-width: thin;
  -ms-overflow-style: auto;

  &::-webkit-scrollbar {
    height: 10px;
    width: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(26, 111, 37, 0.55);
    border-radius: 999px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 999px;
  }

  #scrollContent {
    width: 100%;
    overflow-x: auto;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 2120px;
  background-image: ${({ darkMode }) => (darkMode ? `url(${adminBg})` : 'none')};
  color: ${({ darkMode }) => (darkMode ? '#fff' : '#333')};
`;

const TableHeader = styled.th`
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: ${({ darkMode }) => (darkMode ? '#f2f2f2' : '#2e7d32')};
  color: ${({ darkMode }) => (darkMode ? '#333' : '#fff')};
  padding: 10px;
  font-size: 14px;
  border: 1px solid #ddd;
  text-align: center;
  vertical-align: middle;

  &:first-child {
    position: static;
    left: auto;
    z-index: auto;
    background-color: ${({ darkMode }) => (darkMode ? '#f2f2f2' : '#2e7d32')};
  }

  &[colspan="2"] {
    text-align: center;
    font-weight: bold;
  }
`;

const TableHeaderS = styled.th`
  position: sticky;
  top: 39px;
  z-index: 1;
  background-color: ${({ darkMode }) => (darkMode ? '#1b5e20' : '#4caf50')}; /* Changed to a lighter green for distinction */
  color: #ffffff;
  padding: 0 10px;
  border: 1px solid #ddd;
  font-size: 12px;
  text-align: center;

  &:first-child {
    position: static;
    left: auto;
    z-index: auto;
    background-color: ${({ darkMode }) => (darkMode ? '#1b5e20' : '#4caf50')}; /* Match subheader color */
  }
`;

const TableData = styled.td`
  padding: 5px;
  font-size: 12px;
  border: 1px solid #ddd;
  color: ${({ darkMode }) => (darkMode ? '#f2f2f2' : '#333')};
  transition: background-color 0.3s ease, color 0.3s ease;

  &:first-child {
    position: static;
    left: auto;
    background-color: ${({ darkMode }) => (darkMode ? 'rgb(37, 112, 46)' : '#f2f2f2')};
    z-index: auto;
  }

  &.station-mail {
    min-width: 200px;
    max-width: 200px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &.station-secNum {
    min-width: 120px;
    max-width: 120px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &.station-secDate {
    min-width: 80px;
    max-width: 80px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &.station-iaTin {
    min-width: 120px;
    max-width: 120px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &.station-iaPres {
    min-width: 200px;
    max-width: 200px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const TableRow = styled.tr`
  background: ${({ darkMode }) => (darkMode ? 'rgba(3, 82, 33, 0.3)' : '#fff')};
  backdrop-filter: ${({ darkMode }) => (darkMode ? 'blur(10px)' : 'none')};
  -webkit-backdrop-filter: ${({ darkMode }) => (darkMode ? 'blur(10px)' : 'none')};

  &:hover {
    background-color: ${({ darkMode }) => (darkMode ? 'rgb(37, 112, 46)' : '#f1f1f1')};
    color: ${({ darkMode }) => (darkMode ? '#f2f2f2' : '#333')};
  }

  &:hover td {
    background-image: ${({ darkMode }) => (darkMode ? `url(${adminBg})` : 'none')};
    color: ${({ darkMode }) => (darkMode ? '#fff' : '#333')};
  }

  &:nth-child(even) {
    background-color: ${({ darkMode }) => (darkMode ? 'rgba(3, 82, 33, 0.3)' : '#f9f9f9')};
  }
`;

const RightPanel = styled.div`
  position: fixed;
  top: 0;
  right: ${({ show }) => (show ? '0' : '-45%')};
  width: 43%;
  height: 100%;
  background-color: ${({ darkMode }) => (darkMode ? 'transparent' : '#fff')};
  background-image: ${({ darkMode }) => (darkMode ? `url(${adminBg})` : 'none')};
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  box-shadow: -4px 0 10px rgba(0, 0, 0, 0.2);
  z-index: 5;
  padding: 5px;
  box-sizing: border-box;
  transition: right 0.3s ease-in-out;
`;

const TableContainer = styled.div`
  margin-top: 0;
  padding: 0 20px;
`;